import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, Subscription, catchError, map, throwError } from 'rxjs';

import type { Product } from '../../../shared/models/product.model';
import { ApiService } from '../../../shared/services/api.service';
import { AuthService } from '../../../shared/services/auth.service';
import { toUiProduct } from '../../../shared/utils/product.utils';

/**
 * Tarjeta de `GET /profile/wishlist` (A1, B16): `miniProduct()` + `added_at`. Trae `image` (nombre del fichero), **no**
 * `images[]`, y en lo no comprable llega sin `sale_price`, `taxes`, `discount` ni `discountQty` (precio enmascarado).
 */
export type ProfileWishlistCard = Product & { added_at: string };

/** Una fila de la pestaña: el producto ya mapeado para la UI y cuándo se guardó. */
export interface ProfileWishlistEntry {
  product: Product;
  /** `added_at` tal cual (ISO 8601). */
  addedAt: string;
  /** `added_at` en ms para ordenar; `0` si no se puede leer. */
  addedAtMs: number;
}

/** Fallo tipado: `unauthorized` ya ha lanzado `auth.me()`, que limpia la sesión. */
export type ProfileWishlistFailure = { kind: 'unauthorized' } | { kind: 'error' };

/**
 * `GET /profile/wishlist` (A1 de B10, `docs/api/profile-api.md` §A1), mismo patrón que `ProfileOrdersService`.
 *
 * - **Solo en navegador** (§5.6.6): en SSR no hay petición; `list()` completa sin emitir.
 * - **401 → `auth.me()`** (§5.6.7), una recuperación en vuelo. Nunca `logout()`, que navega a `/`.
 * - **Sin estado** ni caché: lo que se pinta es de una cuenta y lo guarda el componente atado a `currentUser()?.id`.
 *
 * Cada tarjeta pasa por `toUiProduct()`, cuyo `resolveImageUrl()` cae en `image` cuando no hay `images[]`. Sin
 * `images[]` no hay segunda imagen al pasar el ratón (asumido).
 */
@Injectable({ providedIn: 'root' })
export class ProfileWishlistService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private recovery: Subscription | null = null;

  list(): Observable<ProfileWishlistEntry[]> {
    if (!this.isBrowser) return EMPTY;
    return this.api.get<{ data: ProfileWishlistCard[] }>('/profile/wishlist').pipe(
      map((res) => (res.data ?? []).map(toEntry)),
      catchError((err: unknown) => this.fail(err)),
    );
  }

  private fail(err: unknown): Observable<never> {
    if (err instanceof HttpErrorResponse && err.status === 401) {
      if (!this.recovery || this.recovery.closed) this.recovery = this.auth.me().subscribe();
      return throwError((): ProfileWishlistFailure => ({ kind: 'unauthorized' }));
    }
    return throwError((): ProfileWishlistFailure => ({ kind: 'error' }));
  }
}

function toEntry(card: ProfileWishlistCard): ProfileWishlistEntry {
  const ms = Date.parse(card.added_at);
  return { product: toUiProduct(card), addedAt: card.added_at, addedAtMs: Number.isNaN(ms) ? 0 : ms };
}
